import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Button } from '../Button/Button';
import './Tabs.css';

export interface TabItem {
  id: string;
  label: string;
  panel: ReactNode;
  ariaLabel?: string;
}

export interface TabsProps {
  items: TabItem[];
  /** Controlled active tab id. When provided, the component defers to the parent for state. */
  activeId?: string;
  /** Called whenever the active tab changes (via click or keyboard activation). */
  onChange?: (id: string) => void;
  /** Uncontrolled initial active tab id — used only when `activeId` is not provided. */
  defaultActiveId?: string;
  /** Accessible label for the tablist container. */
  'aria-label'?: string;
  className?: string;
}

function getInitialActiveId(items: TabItem[], defaultActiveId?: string): string {
  if (defaultActiveId && items.some(item => item.id === defaultActiveId)) {
    return defaultActiveId;
  }
  return items.length > 0 ? items[0].id : '';
}

export function Tabs({
  items,
  activeId,
  onChange,
  defaultActiveId,
  'aria-label': ariaLabel,
  className,
}: TabsProps) {
  const isControlled = activeId !== undefined;
  const [internalActiveId, setInternalActiveId] = useState<string>(() =>
    getInitialActiveId(items, defaultActiveId),
  );

  const currentActiveId = isControlled ? activeId : internalActiveId;

  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const tablistRef = useRef<HTMLDivElement>(null);

  // Keep the active tab visible when the tab bar scrolls sideways (narrow screens).
  // Only the tablist scrolls — never the page, unlike scrollIntoView.
  useEffect(() => {
    const list = tablistRef.current;
    const tab = tabRefs.current.get(currentActiveId);
    if (!list || !tab) return;
    const left = tab.offsetLeft;
    const right = left + tab.offsetWidth;
    if (left < list.scrollLeft) {
      list.scrollLeft = left;
    } else if (right > list.scrollLeft + list.clientWidth) {
      list.scrollLeft = right - list.clientWidth;
    }
  }, [currentActiveId]);

  const setActive = useCallback(
    (id: string) => {
      if (!isControlled) {
        setInternalActiveId(id);
      }
      onChange?.(id);
    },
    [isControlled, onChange],
  );

  const registerTabRef = useCallback(
    (id: string) => (el: HTMLButtonElement | null) => {
      if (el) {
        tabRefs.current.set(id, el);
      } else {
        tabRefs.current.delete(id);
      }
    },
    [],
  );

  const focusAndActivate = useCallback(
    (id: string) => {
      const el = tabRefs.current.get(id);
      el?.focus();
      setActive(id);
    },
    [setActive],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      const itemCount = items.length;
      let nextIndex: number | null = null;

      switch (event.key) {
        case 'ArrowRight':
          nextIndex = (currentIndex + 1) % itemCount;
          break;
        case 'ArrowLeft':
          nextIndex = (currentIndex - 1 + itemCount) % itemCount;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = itemCount - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      const nextItem = items[nextIndex];
      if (nextItem) {
        focusAndActivate(nextItem.id);
      }
    },
    [items, focusAndActivate],
  );

  return (
    <div className={className ? `tabs-root ${className}` : 'tabs-root'}>
      <div ref={tablistRef} className="tabs-tablist" role="tablist" aria-label={ariaLabel}>
        {items.map((item, index) => {
          const isSelected = item.id === currentActiveId;
          const tabId = `tab-${item.id}`;
          const panelId = `tabpanel-${item.id}`;

          return (
            <Button
              key={item.id}
              ref={registerTabRef(item.id)}
              id={tabId}
              type="button"
              role="tab"
              variant="ghost"
              aria-selected={isSelected}
              aria-controls={panelId}
              tabIndex={isSelected ? 0 : -1}
              className="tabs-tab"
              aria-label={item.ariaLabel}
              onClick={() => setActive(item.id)}
              onKeyDown={event => handleKeyDown(event, index)}
            >
              {item.label}
            </Button>
          );
        })}
      </div>
      {items.map(item => {
        const tabId = `tab-${item.id}`;
        const panelId = `tabpanel-${item.id}`;
        const isSelected = item.id === currentActiveId;

        return (
          <div
            key={item.id}
            id={panelId}
            role="tabpanel"
            aria-labelledby={tabId}
            className="tabs-tabpanel"
            hidden={!isSelected}
          >
            {item.panel}
          </div>
        );
      })}
    </div>
  );
}
